import {
  Box,
  Button,
  Card,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  CircularProgress,
} from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import SearchBar from '../SearchBar.jsx'
import ImportNewAppButton from './ImportNewAppButton.jsx'
import { API_BASE } from '../../setupAxios'

const AppZoo = () => {
  const [searchText, setSearchText] = useState('')
  const [cardsPerRow, setCardsPerRow] = useState(3)

  const [apps, setApps] = useState([])
  const [appStates, setAppStates] = useState({})

  const setAppState = useCallback((id, patch) => {
    setAppStates(prev => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }))
  }, [])

  // -------------------------
  // LOAD APPS
  // -------------------------
  const fetchApps = useCallback(async () => {
    try {
      const res = await axios.get('/apps')

      setApps(res.data)

      setAppStates(
        Object.fromEntries(
          res.data.map(app => [
            app.id,
            {
              status:
                app.status === 'building'
                  ? 'loading' // Show spinning wheel while building
                  : app.status || 'stopped',
              url:
                app.status === 'running'
                  ? `http://127.0.0.1:${app.port}`
                  : null,
            },
          ])
        )
      )
    } catch (err) {
      console.error('Error fetching apps:', err)
    }
  }, [])

  useEffect(() => {
    fetchApps()
  }, [fetchApps])

  // -------------------------
  // HEARTBEAT (ONLY WHEN VISIBLE)
  // -------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return

      Object.entries(appStates).forEach(([id, state]) => {
        if (state.status === 'running') {
          axios.post(`/apps/${id}/heartbeat`).catch(() => {})
        }
      })
    }, 60_000)

    return () => clearInterval(interval)
  }, [appStates])

  // -------------------------
  // STATUS SYNC (DETECT BACKEND STOPS / BUILDING COMPLETION)
  // -------------------------
  useEffect(() => {
    const interval = setInterval(async () => {
      if (document.visibilityState !== 'visible') return

      const checkIds = Object.entries(appStates)
        .filter(([, state]) => state.status === 'running' || state.status === 'loading')
        .map(([id]) => id)

      for (const id of checkIds) {
        try {
          const res = await axios.get(`/apps/${id}/status`)
          
          // Map backend status to frontend status
          let newStatus = res.data.status
          if (newStatus === 'building') {
            newStatus = 'loading'
          }
          
          if (newStatus !== appStates[id].status) {
            const url = newStatus === 'running' ? res.data.url : null
            setAppState(id, { status: newStatus, url })
          }
        } catch {
          // ignore transient errors
        }
      }
    }, 5_000) // Check more frequently for building status

    return () => clearInterval(interval)
  }, [appStates, setAppState])

  // -------------------------
  // TAB CLOSE (SOFT SIGNAL)
  // -------------------------
  useEffect(() => {
    const handleUnload = () => {
      Object.entries(appStates).forEach(([id, state]) => {
        if (state.status === 'running') {
          navigator.sendBeacon(`/apps/${id}/heartbeat?final=true`)
        }
      })
    }

    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [appStates])

  // -------------------------
  // CLICK APP (START / OPEN)
  // -------------------------
  const handleCardClick = async app => {
    const current = appStates[app.id]

    if (current?.status === 'loading') return

    setAppState(app.id, { status: 'loading' })

    try {
      const statusRes = await axios.get(`/apps/${app.id}/status`)

      let url = statusRes.data.url

      // still building
      if (statusRes.data.status === 'building') {
        setAppState(app.id, {
          status: 'loading',
        })
        return
      }

      // build failed
      if (statusRes.data.status === 'build_failed') {
        setAppState(app.id, {
          status: 'stopped',
        })
        alert('App failed to build. Please try re-importing it.')
        return
      }

      // already running
      if (statusRes.data.status === 'running') {
        setAppState(app.id, {
          status: 'running',
          url,
        })

        window.open(url, '_blank')
        axios.post(`/apps/${app.id}/heartbeat`).catch(() => {})
        return
      }

      // start app
      const startRes = await axios.post(`/apps/${app.id}/start`)
      url = startRes.data.url

      setAppState(app.id, {
        status: 'running',
        url,
      })

      window.open(url, '_blank')
      axios.post(`/apps/${app.id}/heartbeat`).catch(() => {})
    } catch (e) {
      console.error('Failed to start app', e)

      setAppState(app.id, {
        status: 'stopped',
        url: null,
      })
    }
  }

  // -------------------------
  // STOP APP
  // -------------------------
  const handleStop = async (e, app) => {
    e.stopPropagation()

    setAppState(app.id, { status: 'loading' })

    try {
      await axios.post(`/apps/${app.id}/stop`)

      setAppState(app.id, {
        status: 'stopped',
        url: null,
      })
    } catch (e) {
      console.error(e)

      // rollback if failure
      setAppState(app.id, {
        status: 'running',
      })
    }
  }

  // -------------------------
  // FILTER
  // -------------------------
  const filteredApps = apps.filter(app =>
    app.name.toLowerCase().includes(searchText.toLowerCase())
  )

  return (
    <Box
      sx={{
        mx: 'auto',
        mt: 1,
        borderRadius: 4,
        width: { xs: '80%', sm: '70%' },
        display: 'flex',
        flexDirection: 'column',
        p: 1,
      }}
    >
      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, mt: 2 }}>
        <Typography variant="h3" fontWeight={700} mt={2}>
          App Zoo
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Browse and test applications, or share your own!
        </Typography>

        <Box sx={{ ml: 'auto', transform: 'scale(1.2)' }}>
          <ImportNewAppButton refreshApps={fetchApps} />
        </Box>
      </Box>

      {/* SEARCH */}
      <Box mt={4}>
        <SearchBar
          searchText={searchText}
          setSearchText={setSearchText}
          placeholder="Search apps"
        />
      </Box>

      {/* GRID CONTROL */}
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2">Cards per row:</Typography>

        <ToggleButtonGroup
          value={cardsPerRow}
          exclusive
          onChange={(_, val) => val !== null && setCardsPerRow(val)}
          size="small"
        >
          {[2, 3, 4].map(n => (
            <ToggleButton key={n} value={n}>
              {n}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* GRID */}
      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
          gap: 2,
        }}
      >
        {filteredApps.map(app => {
          const state = appStates[app.id] || {
            status: 'stopped',
            url: null,
          }

          const isRunning = state.status === 'running'
          const isLoading = state.status === 'loading'

          return (
            <Card
              key={app.id}
              elevation={0}
              onClick={() => handleCardClick(app)}
              sx={{
                border: 2,
                borderColor: isRunning ? '#16a4b0' : 'rgb(243,243,243)',
                borderRadius: 6,
                p: 2,
                cursor: isLoading ? 'wait' : 'pointer',
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                },
              }}
            >
              {/* STATUS */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                {isLoading && <CircularProgress size={16} />}

                {isRunning && !isLoading && (
                  <>
                    <Chip
                      label="Running"
                      size="small"
                      sx={{
                        backgroundColor: '#e0f7f9',
                        color: '#16a4b0',
                      }}
                    />

                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={e => handleStop(e, app)}
                    >
                      Stop
                    </Button>
                  </>
                )}
              </Box>

              <Typography fontWeight={600} textAlign="center" mb={2}>
                {app.name}
              </Typography>

              <Box
                component="img"
                src={
                  app.preview_image
                    ? `${API_BASE}/uploads/${app.preview_image}`
                    : '/app_default_preview.png'
                }
                sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  objectFit: 'cover',
                  borderRadius: 4,
                  opacity: isLoading ? 0.5 : 1,
                }}
              />
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}

export default AppZoo